import uuidv1 from "uuid/v1";
import debounce from "lodash.debounce";
import { cookiesSupported } from "../../utilities";
import { fetchCSRFToken } from "../../csrf";
import decoratePlayerItem from "../playables/decorate";

let latestRequestID = null;

/**
 * Debounce any PUT requests
 * This needs to be bound separately so it's a consistent variable
 */
const debouncedPut = debounce(
  (state, actions) => {
    // Clear out the latest GET request ID
    latestRequestID = null;
    window.setTimeout(actions.put, 0);
  },
  5,
  { trailing: true }
);

export default {
  set: partialState => partialState,
  // Update the nested value with whatever is set here
  setUpdateItems: upcomingItems => {
    const currentItem = upcomingItems[0];
    upcomingItems = upcomingItems.slice(1);
    return {
      currentItem,
      upcomingItems
    };
  },
  // Pre-resolve any items before they're played
  updateItems: newItems => async (state, actions) => {
    const decoratedItems = await Promise.all(
      newItems.map(async i => {
        return i != null ? await decoratePlayerItem(i) : i;
      })
    );
    actions.setUpdateItems(decoratedItems);
  },
  //
  queueFetch: request => async (state, actions) => {
    const requestID = uuidv1();
    latestRequestID = requestID;
    request
      .then(response => {
        if (!response.ok) {
          throw response;
        }
        return response.json();
      })
      .then(items => {
        if (latestRequestID === requestID) {
          actions.updateItems(items);
        }
      });
  },
  // Trigger a fetch
  fetch: () => (state, actions) => {
    fetchCSRFToken().then(csrfToken => {
      const timestamp = Date.now().toString();
      actions.queueFetch(
        fetch(`/queue.json?t=${timestamp}`, {
          method: "GET",
          credentials: "include",
          headers: {
            accept: "application/json"
          }
        })
      );
    });
  },
  requestPut: () => debouncedPut,
  put: () => async (state, actions) => {
    const { currentItem, upcomingItems } = state;
    let items = [];
    if (currentItem) {
      items = items.concat([currentItem]);
    }
    items = items.concat(upcomingItems).map(item => {
      // Only send ids for already created items
      // Shared audio items must send their full data
      const sendableItem = item._copy || item;
      delete sendableItem.sharedMomentBaseURL;
      if (
        sendableItem.player_item_id &&
        sendableItem.type !== "shared_audio_item"
      ) {
        const { player_item_id, id } = sendableItem;
        return { player_item_id, id };
      } else {
        // Use the ID from the top level item as its where we refresh them
        return { ...sendableItem, id: item.id };
      }
    });

    const csrfToken = await fetchCSRFToken();
    const timestamp = Date.now().toString();
    actions.queueFetch(
      fetch(`/queue.json?t=${timestamp}`, {
        method: "PUT",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          "x-csrf-token": csrfToken,
          "x-cookies-supported": cookiesSupported
        },
        body: JSON.stringify({ items })
      })
    );
  },
  setCurrent: items => async (state, actions) => {
    // Setting current adds the item next in the queue
    items = Array.isArray(items) ? items : [items];
    await actions.prepend(items);
    // ... and then moves the queue forward
    await actions.moveTo(1);
  },
  prepend: itemsToPrepend => async (state, actions) => {
    const { currentItem } = state;
    itemsToPrepend = Array.isArray(itemsToPrepend)
      ? itemsToPrepend
      : [itemsToPrepend];
    let upcomingItems = state.upcomingItems.slice();
    upcomingItems = itemsToPrepend.concat(upcomingItems);
    let items = [];
    items = items.concat([currentItem]).concat(upcomingItems);
    await actions.updateItems(items);
    actions.requestPut();
  },
  append: itemsToAppend => async (state, actions) => {
    const { currentItem } = state;
    itemsToAppend = Array.isArray(itemsToAppend)
      ? itemsToAppend
      : [itemsToAppend];
    let upcomingItems = state.upcomingItems.slice();
    upcomingItems = upcomingItems.concat(itemsToAppend);
    let items = [];
    items = items.concat([currentItem]).concat(upcomingItems);
    await actions.updateItems(items);
    actions.requestPut();
  },
  remove: id => async (state, actions) => {
    const { currentItem, upcomingItems } = state;
    let items = [];
    if (currentItem) {
      items = items.concat([currentItem]);
    }
    items = items.concat(upcomingItems);
    // Get index
    const indexToRemove = items.indexOf(items.find(item => item.id === id));
    if (indexToRemove > -1) {
      // Mutate
      items.splice(indexToRemove, 1);
      await actions.updateItems(items);
      actions.requestPut();
    }
  },
  record: items => (state, actions) => {
    let playedItems = state.playedItems.concat(items);
    actions.set({
      playedItems
    });
  },
  next: () => (state, actions) => {
    actions.moveTo(1);
  },
  prev: () => (state, actions) => {
    actions.moveTo(-1);
  },
  reorder: ({ fromIndex, toIndex }) => async (state, actions) => {
    const { currentItem, upcomingItems } = state;
    let items = [];
    if (currentItem) {
      items = items.concat([currentItem]);
    }
    items = items.concat(upcomingItems);
    const plucked = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, plucked[0]);
    await actions.updateItems(items);
    actions.requestPut();
  },
  // Takes a set of indices matches against the current set and reorders
  updateOrder: newIndices => (state, actions) => {
    const { currentItem, upcomingItems } = state;
    let items = [];
    let offset = 0;
    if (currentItem) {
      items = items.concat([currentItem]);
    }
    items = items.concat(upcomingItems);
    let updatedItems = [];
    // Set an offset when track listing doesn't include currentItem
    if (items.length > newIndices.length) {
      updatedItems.push(currentItem);
      offset = 1;
    }
    newIndices.forEach(i => {
      updatedItems.push(items[i + offset]);
    });
    actions.updateItems(updatedItems);
    actions.requestPut();
  },
  // Move the queue to passed index
  moveTo: (index = 1) => async (state, actions) => {
    let upcomingItems = state.upcomingItems.slice();
    if (index > 0) {
      // Move the playhead down the queue
      // Mutate: remove up to index
      const skippedItems = upcomingItems.splice(0, index);
      const playedItems = state.playedItems.concat([state.currentItem]);
      // Mutate: assign _last_ item from skippedItems as currentItem
      const currentItem = skippedItems.splice(skippedItems.length - 1, 1)[0];
      // Update non-played items separately to they’re resolved
      let items = [];
      if (currentItem) {
        items = items.concat([currentItem]);
      }
      items = items.concat(upcomingItems);
      await actions.updateItems(items);
      actions.requestPut();
      actions.set({
        playedItems: playedItems.concat(skippedItems)
      });
    } else if (index < 0) {
      // Retrieve any history we can
      let playedItems = state.playedItems.slice().reverse();
      // Mutate: remove retrievedHistory from playedItems
      const retrievedHistory = playedItems.splice(0, Math.abs(index));
      // Mutate: Retrieve current item and remove from retrievedHistory
      const newCurrentItem = retrievedHistory.splice(0, 1)[0];
      // Reconstruct upcomingItems in the right order:
      // - Any retrieved history (will usually be none)
      // - The current playing item
      // - Existing upcomingItems
      upcomingItems = retrievedHistory
        .concat([state.currentItem])
        .concat(upcomingItems);
      let items = [];
      if (newCurrentItem) {
        items = items.concat([newCurrentItem]);
      }
      items = items.concat(upcomingItems);
      // Update non-played items separately to they’re resolved
      await actions.updateItems(items);
      actions.requestPut();
      actions.set({
        playedItems: playedItems.reverse()
      });
    }
  },
  clear: (excludeCurrent = false) => async (state, actions) => {
    actions.requestPut();
    const items = excludeCurrent ? [state.currentItem] : [];
    await actions.updateItems(items);
  }
};
